import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductPath } from '../utils/productUrl';
import { isProductInStock } from '../utils/catalogProductShape';
import { formatOfferPrice } from '../utils/productOffers';
import { getListProductId } from '../utils/myList';
import {
  getCompareProductId,
  areAllOffersInCompare,
  canAddOffersToCompare,
  filterCompareEligibleOffers,
} from '../utils/compareProducts';
import {
  trackCheckAvailability,
  trackAddToCompareEvent,
  trackSaveDeal,
  trackPriceMatchBadgeView,
} from '../utils/analyticsTracker';
import {
  getCardOfferStatus,
  getOfferStatusLabel,
  isOfferExpired,
} from '../utils/offerStatus';
import { productCardImageUrl } from '../utils/cloudinaryImage';
import {
  applyImageFailoverChain,
  resolveProductCardImageSrc,
} from '../utils/productImageFallback';
import CompareScaleIcon from './CompareScaleIcon';
import { useStorefrontSettings } from '../context/StorefrontSettingsContext';
import {
  formatPriceMatchSavings,
  formatPriceMatchShortStore,
  isPriceMatchVisible,
} from '../utils/hotpointPriceMatch';
import './ProductCard.css';

const PriceMatchIcon = ({ className = 'price-match-icon' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const ProductCard = ({
  product,
  onAddToList,
  onAddToCart,
  onAddToCompare,
  onCheckAvailability,
  listProductIds,
  compareProductIds,
  onRemoveFromList,
  eagerImage = false,
}) => {
  const handleAddToList = onAddToList || onAddToCart;
  const navigate = useNavigate();
  const { showPriceMatch, imagekitPrimaryEnabled } = useStorefrontSettings();
  const [imageError, setImageError] = useState(false);
  const priceMatch = isPriceMatchVisible(product?.priceMatch, showPriceMatch) ? product.priceMatch : null;
  const trackedPriceMatchRef = useRef(null);

  useEffect(() => {
    if (!product || !priceMatch) return;
    const key = `${product._id || product.id}:${priceMatch.savingsAmount}`;
    if (trackedPriceMatchRef.current === key) return;
    trackedPriceMatchRef.current = key;
    trackPriceMatchBadgeView(product, priceMatch, { source: 'card' });
  }, [product, priceMatch]);

  const offerGroup = product?.offerGroup;
  const isMultiStore = Boolean(offerGroup && offerGroup.storeCount > 1);
  const offerStatus = getCardOfferStatus(product);
  const isExpired = offerStatus === 'expired';
  const statusLabel = getOfferStatusLabel(offerStatus);

  const getProductImage = (item) =>
    resolveProductCardImageSrc(item, { preferImageKit: imagekitPrimaryEnabled }) ||
    productCardImageUrl(
      item?.thumbnail ||
        (item?.images && item.images.length
          ? item.images[
              item.primaryImageIndex !== undefined && item.primaryImageIndex < item.images.length
                ? item.primaryImageIndex
                : 0
            ]
          : item?.image || '')
    );

  const listId = getListProductId(product);
  const inList = Boolean(listProductIds?.has(listId));

  const groupOffers = isMultiStore && offerGroup?.offers?.length ? offerGroup.offers : [product];
  const compareEligibleOffers = filterCompareEligibleOffers(groupOffers);
  const inCompare = isMultiStore
    ? areAllOffersInCompare(groupOffers, compareProductIds)
    : Boolean(compareProductIds?.has(getCompareProductId(product)));

  const inStock =
    product.inStock !== undefined ? product.inStock !== false : isProductInStock(product);
  const displayPrice = isMultiStore ? offerGroup.fromPrice : product.price;
  const showOriginal =
    !product.hideCompareAtPrice &&
    product.originalPrice &&
    product.originalPrice > displayPrice;

  const discountBadge =
    isMultiStore && offerGroup.maxDiscount > 0 ? offerGroup.maxDiscount : product.discount;

  const canCheckAvailability = !isExpired && inStock;
  const canCompare = isMultiStore
    ? canAddOffersToCompare(groupOffers, compareProductIds, compareProductIds?.size ?? 0)
    : !isExpired && inStock && !inCompare;
  const compareButtonLabel = isMultiStore
    ? inCompare
      ? 'All stores in compare'
      : `Compare ${compareEligibleOffers.length} stores`
    : 'Compare product';

  const handleProductClick = (e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    navigate(getProductPath(product));
  };

  const handleHeartClick = (e) => {
    e.stopPropagation();
    if (inList) {
      onRemoveFromList?.(listId);
      return;
    }
    if (handleAddToList) {
      trackSaveDeal(product);
      handleAddToList(product, e.currentTarget.getBoundingClientRect());
    }
  };

  const handleCheckAvailabilityClick = (e) => {
    e.stopPropagation();
    if (!canCheckAvailability) return;
    if (isMultiStore) {
      navigate(getProductPath(product));
      return;
    }
    trackCheckAvailability(product, { source: 'card' });
    onCheckAvailability?.(product);
  };

  const handleCompareClick = (e) => {
    e.stopPropagation();
    if (!canCompare || !onAddToCompare) return;
    const toCompare = isMultiStore ? compareEligibleOffers : [product];
    trackAddToCompareEvent(toCompare, { source: 'card' });
    onAddToCompare(toCompare, e.currentTarget.getBoundingClientRect());
  };

  const storeLabel =
    isMultiStore
      ? `${offerGroup.storeCount} stores`
      : product.storeName || product.vendor || '';

  return (
    <div
      className={[
        'product-card',
        !inStock && !isMultiStore ? 'product-card--out-of-stock' : '',
        isMultiStore ? 'product-card--multi-store' : '',
        isExpired ? 'product-card--expired' : '',
        priceMatch ? 'product-card--price-match' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleProductClick}
    >
      <div className="product-image-container">
        {imageError ? (
          <div className="product-image-placeholder">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            <span>Image Coming Soon</span>
          </div>
        ) : (
          <img
            src={getProductImage(product)}
            alt={product.name}
            className="product-image"
            referrerPolicy="no-referrer"
            loading={eagerImage ? 'eager' : 'lazy'}
            fetchPriority={eagerImage ? 'high' : 'auto'}
            decoding="async"
            onError={(e) => {
              if (imageError) return;
              const switched = applyImageFailoverChain(e.currentTarget, product, {
                preferImageKit: imagekitPrimaryEnabled,
              });
              if (!switched) setImageError(true);
            }}
          />
        )}
        {discountBadge > 0 ? (
          <div className="discount-badge">{discountBadge}% OFF</div>
        ) : null}
        <button
          type="button"
          className={`product-save-btn${inList ? ' product-save-btn--saved' : ''}`}
          aria-label={inList ? 'Remove from saved deals' : 'Save deal'}
          onClick={handleHeartClick}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill={inList ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>
        {!inStock && !isMultiStore ? (
          <div className="out-of-stock-badge">Out of stock</div>
        ) : null}
        {priceMatch ? (
          <>
            <div className="price-match-badge">
              <PriceMatchIcon />
              <span>Price Match</span>
              <span className="amount">{formatPriceMatchSavings(priceMatch.savingsAmount, priceMatch.currency)}</span>
            </div>
            <div className="price-match-pill">
              <PriceMatchIcon />
              <span>{formatPriceMatchSavings(priceMatch.savingsAmount, priceMatch.currency)}</span>
            </div>
          </>
        ) : null}
      </div>

      <div className="product-info">
        {product.brand ? <p className="product-brand">{product.brand}</p> : null}
        <h3 className="product-name">{product.name}</h3>
        {storeLabel ? <p className="product-store-line">{storeLabel}</p> : null}
        {priceMatch ? (
          <div className="price-match-chip">
            <PriceMatchIcon />
            <span>Price Match</span>
          </div>
        ) : null}

        <div className="product-pricing">
          <span className="current-price">
            {isMultiStore ? <span className="price-from-label">From </span> : null}
            {formatOfferPrice(displayPrice)}
          </span>
          {showOriginal ? (
            <span className="original-price">{formatOfferPrice(product.originalPrice)}</span>
          ) : null}
          {statusLabel ? (
            <span className={`offer-status-badge offer-status-badge--${offerStatus}`}>
              {statusLabel}
            </span>
          ) : null}
        </div>

        {priceMatch ? (
          <div className="price-match-strip">
            <div className="icon" aria-hidden>
              <PriceMatchIcon className="price-match-icon price-match-icon--strip" />
            </div>
            <div>
              <strong>Cheaper at {formatPriceMatchShortStore(priceMatch.competitorStoreName)}</strong>
              <span>
                Pay {formatOfferPrice(priceMatch.competitorPrice)} with Hotpoint Price Match
              </span>
            </div>
          </div>
        ) : null}

        <div className="product-actions">
          <button
            type="button"
            className="check-availability-btn"
            disabled={!canCheckAvailability}
            onClick={handleCheckAvailabilityClick}
          >
            {!canCheckAvailability
              ? isExpired
                ? 'Unavailable'
                : 'Out of stock'
              : 'Check availability'}
          </button>
          <button
            type="button"
            className={`compare-product-btn${inCompare ? ' compare-product-btn--active' : ''}`}
            disabled={!canCompare}
            title={inCompare ? 'In compare' : compareButtonLabel}
            aria-label={inCompare ? 'In compare' : compareButtonLabel}
            onClick={handleCompareClick}
          >
            <CompareScaleIcon className="compare-product-btn-icon" size={16} />
            {compareButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
